import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import {
  IProfessional,
  IDentalTreatment,
  IDentalTreatmentItem,
} from '../../../shared/interfaces';
import api from '../../../services/api';
import {
  AButton,
  ATitle,
  Loading,
  MenuOptions,
  ItemStatus,
} from '../../../components';
import {
  CheckCircleOutlined,
  HistoryOutlined,
  LineChartOutlined,
  FilterOutlined,
  PrinterOutlined,
  RedoOutlined,
  SyncOutlined,
  UserSwitchOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  Card,
  Col,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { currency } from '../../../shared/utils/functions';
import EditEvolution from './Edit';
import HistoricTreatment from './Historic';

interface IDentalTreatmentEvolutionLog {
  id: string;
  dental_treatment_item_id?: string;
  procedure_id?: string;
  date?: string;
  time?: string;
  created_at?: string;
  signature_validation_code?: string | null;
  signed_at?: string | null;
}

interface ISignatureInfo {
  validationCode: string | null;
  signedAt: string | null;
}

const DentalTreatmentEvolution: React.FC = () => {
  type TStatusFilter = 'all' | '0' | '1' | '2';

  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [treatment, setTreatment] = useState<IDentalTreatment | null>(null);
  const [typeEdit, setTypeEdit] = useState<number>();
  const [treatmentItems, setTreatmentItems] = useState<IDentalTreatmentItem[]>(
    [],
  );
  const [treatmentItem, setTreatmentItem] =
    useState<IDentalTreatmentItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalHistoric, setModalHistoric] = useState<boolean>(false);
  const [modalEditEvolution, setModalEditEvolution] = useState<boolean>(false);
  const [updateProfessional, setUpdateProfessional] = useState<boolean>(false);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [statusFilter, setStatusFilter] = useState<TStatusFilter>('all');
  const [signatureByItem, setSignatureByItem] = useState<
    Record<string, ISignatureInfo>
  >({});
  const [signatureByProcedure, setSignatureByProcedure] = useState<
    Record<string, ISignatureInfo>
  >({});
  const [procedureCountMap, setProcedureCountMap] = useState<
    Record<string, number>
  >({});

  const summary = useMemo(() => {
    const totalItems = treatmentItems.length;
    const inProgress = treatmentItems.filter(
      (item) => item.status === 1,
    ).length;
    const completed = treatmentItems.filter((item) => item.status === 2).length;
    const pending = totalItems - inProgress - completed;
    const averageRank =
      totalItems > 0
        ? treatmentItems.reduce(
            (acc, item) => acc + Number(item.rank || 0),
            0,
          ) / totalItems
        : 0;

    return {
      totalItems,
      inProgress,
      completed,
      pending,
      averageRank,
    };
  }, [treatmentItems]);

  const filteredItems = useMemo(() => {
    if (statusFilter === 'all') return treatmentItems;
    return treatmentItems.filter(
      (item) => item.status === Number(statusFilter),
    );
  }, [treatmentItems, statusFilter]);

  const currentFilterLabel = useMemo(() => {
    if (statusFilter === 'all') return 'Todos';
    if (statusFilter === '0') return 'Pendentes';
    if (statusFilter === '1') return 'Em andamento';
    return 'Concluídos';
  }, [statusFilter]);

  const loadTreatment = useCallback(async () => {
    setLoading(true);
    try {
      const [treatmentResponse, logsResponse] = await Promise.all([
        api.get(`/dental-treatments/${id}`),
        api.get(`/dental-treatments-logs/list/${id}`),
      ]);

      const { treatment, items } = treatmentResponse.data;
      const logs = (logsResponse.data || []) as IDentalTreatmentEvolutionLog[];

      const nextProcedureCountMap = (items as IDentalTreatmentItem[]).reduce(
        (acc: Record<string, number>, item: IDentalTreatmentItem) => {
          const key = String(item.procedure_id || '');
          if (!key) return acc;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        {},
      );

      const latestByItem: Record<string, IDentalTreatmentEvolutionLog> = {};
      const latestByProcedure: Record<string, IDentalTreatmentEvolutionLog> =
        {};

      logs.forEach((log) => {
        const itemId = String(log.dental_treatment_item_id || '');
        const procedureId = String(log.procedure_id || '');

        if (itemId) {
          const currentByItem = latestByItem[itemId];
          if (!currentByItem) {
            latestByItem[itemId] = log;
          } else {
            const currentByItemTime = new Date(
              `${currentByItem.date || currentByItem.created_at || ''}T${
                currentByItem.time || '00:00:00'
              }`,
            ).valueOf();
            const nextByItemTime = new Date(
              `${log.date || log.created_at || ''}T${log.time || '00:00:00'}`,
            ).valueOf();

            if (nextByItemTime >= currentByItemTime) {
              latestByItem[itemId] = log;
            }
          }
        }

        if (!procedureId) return;

        const current = latestByProcedure[procedureId];
        if (!current) {
          latestByProcedure[procedureId] = log;
          return;
        }

        const currentTime = new Date(
          `${current.date || current.created_at || ''}T${current.time || '00:00:00'}`,
        ).valueOf();
        const nextTime = new Date(
          `${log.date || log.created_at || ''}T${log.time || '00:00:00'}`,
        ).valueOf();

        if (nextTime >= currentTime) {
          latestByProcedure[procedureId] = log;
        }
      });

      const nextSignatureByItem: Record<string, ISignatureInfo> = {};
      Object.entries(latestByItem).forEach(([itemId, log]) => {
        nextSignatureByItem[itemId] = {
          validationCode: log.signature_validation_code || null,
          signedAt: log.signed_at || null,
        };
      });

      const nextSignatureByProcedure: Record<string, ISignatureInfo> = {};
      Object.entries(latestByProcedure).forEach(([procedureId, log]) => {
        nextSignatureByProcedure[procedureId] = {
          validationCode: log.signature_validation_code || null,
          signedAt: log.signed_at || null,
        };
      });

      setTreatment(treatment);
      setTreatmentItems(items);
      setSignatureByItem(nextSignatureByItem);
      setSignatureByProcedure(nextSignatureByProcedure);
      setProcedureCountMap(nextProcedureCountMap);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          error?.response?.data?.message || 'Erro ao carregar o tratamento.',
      });
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  const listProfessionals = useCallback(async () => {
    try {
      const response = await api.get(`/professionals/select`);
      setProfessionals(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    }
  }, [addToast]);

  const handleEdit = useCallback((type: number, item: IDentalTreatmentItem) => {
    setTreatmentItem(item);
    setUpdateProfessional(false);
    setTypeEdit(type);
    setModalEditEvolution(true);
  }, []);

  const handleUpdateProfessional = useCallback((item: IDentalTreatmentItem) => {
    setUpdateProfessional(true);
    setTreatmentItem(item);
    setModalEditEvolution(true);
  }, []);

  const renderModalEditEvolution = () => {
    if (modalEditEvolution) {
      return (
        <EditEvolution
          open={modalEditEvolution}
          close={() => setModalEditEvolution(false)}
          professionals={professionals}
          type={typeEdit}
          treatmentItem={treatmentItem}
          updateProfessional={updateProfessional}
          setTreatmentItems={setTreatmentItems}
        />
      );
    }
  };

  const renderModalHistoricTreatment = () => {
    if (modalHistoric) {
      return (
        <HistoricTreatment
          open={modalHistoric}
          close={() => setModalHistoric(false)}
          treatment={treatment}
        />
      );
    }
  };

  useEffect(() => {
    loadTreatment();
    listProfessionals();
  }, [loadTreatment, listProfessionals]);

  if (loading) return <Loading load />;

  return (
    <>
      <ATitle icon={<LineChartOutlined />}>
        Evolução do tratamento odontológico Nº {treatment?.code}
      </ATitle>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('all')}
            style={
              statusFilter === 'all'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Procedimentos" value={summary.totalItems} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('2')}
            style={
              statusFilter === '2'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Concluídos" value={summary.completed} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('1')}
            style={
              statusFilter === '1'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Em andamento" value={summary.inProgress} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('0')}
            style={
              statusFilter === '0'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Pendentes" value={summary.pending} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Rank médio geral"
              value={summary.averageRank.toFixed(1)}
            />
          </Card>
        </Col>
      </Row>

      <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Space direction="vertical" size={8}>
          <Segmented
            options={[
              { label: 'Todos', value: 'all' },
              { label: 'Pendentes', value: '0' },
              { label: 'Em andamento', value: '1' },
              { label: 'Concluídos', value: '2' },
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as TStatusFilter)}
          />
          <Typography.Text type="secondary">
            <FilterOutlined /> Filtro atual: {currentFilterLabel} (
            {filteredItems.length})
          </Typography.Text>
        </Space>
        <MenuOptions
          menu_items={[
            {
              label: 'Atualizar',
              key: '1',
              icon: <RedoOutlined />,
              onClick: () => loadTreatment(),
            },
            {
              label: 'Histórico',
              key: '2',
              icon: <HistoryOutlined />,
              onClick: () => {
                setModalHistoric(true);
              },
            },
            {
              label: 'Imprimir evolução',
              key: '3',
              icon: <PrinterOutlined />,
              onClick: () => {
                navigate(`/dental/${id}/evolution/print`);
              },
            },
          ]}
          showButtonBack
        />
      </Row>
      <Table
        style={{ width: '100%', marginTop: 20 }}
        scroll={{ x: 1100, y: 350 }}
        columns={[
          {
            title: 'Dente',
            dataIndex: 'tooth_number',
            width: 90,
            fixed: 'left' as const,
            render: (val) => val || '-',
          },
          {
            title: 'Face',
            dataIndex: 'face',
            width: 90,
            render: (val) => val || '-',
          },
          {
            title: 'Procedimento',
            dataIndex: 'procedure_name',
            ellipsis: true,
            width: 'auto',
            render: (val) => val || '-',
          },
          {
            title: 'Profissional',
            dataIndex: 'professional_id',
            width: 160,
            ellipsis: true,
            render: (value: string) => {
              const name = professionals.find((x) => x.id === value)?.name;
              return <>{name ?? ''}</>;
            },
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (value: number) => <ItemStatus type={value} />,
          },
          {
            title: 'Total',
            width: 110,
            render: (cell: IDentalTreatmentItem) => (
              <>{currency((cell.value || 0) - (cell.discount || 0))}</>
            ),
          },
          {
            title: 'Assinatura',
            width: 130,
            render: (cell: IDentalTreatmentItem) => {
              const itemSignature = signatureByItem[String(cell.id)];
              if (itemSignature?.validationCode || itemSignature?.signedAt) {
                return (
                  <Tooltip
                    title={`Código: ${itemSignature.validationCode || '—'}`}
                  >
                    <Tag color="success" icon={<SafetyCertificateOutlined />}>
                      Assinado
                    </Tag>
                  </Tooltip>
                );
              }

              const procedureKey = String(cell.procedure_id || '');
              const sameProcedureItems = procedureCountMap[procedureKey] || 0;
              const isUniqueProcedure = sameProcedureItems <= 1;
              const signatureInfo = signatureByProcedure[procedureKey];
              const isSigned = Boolean(
                signatureInfo?.validationCode || signatureInfo?.signedAt,
              );
              const isSignedByItem = Boolean(
                cell.signature_validation_code || cell.signed_at,
              );

              if (!isUniqueProcedure && !isSignedByItem) {
                return (
                  <Tooltip title="Assinatura por log ambígua para procedimento repetido neste tratamento.">
                    <Tag color="default">Não assinado</Tag>
                  </Tooltip>
                );
              }

              return isSignedByItem || isSigned ? (
                <Tooltip
                  title={`Código: ${
                    cell.signature_validation_code ||
                    signatureInfo?.validationCode ||
                    '—'
                  }`}
                >
                  <Tag color="success" icon={<SafetyCertificateOutlined />}>
                    Assinado
                  </Tag>
                </Tooltip>
              ) : (
                <Tag color="default">Não assinado</Tag>
              );
            },
          },
          {
            title: 'Opções',
            width: 190,
            fixed: 'right' as const,
            render: (cell: IDentalTreatmentItem) => (
              <Space size={4} wrap>
                <AButton
                  type="primary"
                  onClick={() => handleEdit(1, cell)}
                  icon={<SyncOutlined />}
                  title="Em andamento"
                />
                <AButton
                  type="primary"
                  style={{ backgroundColor: '#32cd32' }}
                  onClick={() => handleEdit(2, cell)}
                  icon={<CheckCircleOutlined />}
                  title="Concluído"
                />
                <AButton
                  type="dashed"
                  onClick={() => handleUpdateProfessional(cell)}
                  icon={<UserSwitchOutlined />}
                  title="Alterar Profissional"
                />
                <AButton
                  type="default"
                  onClick={() =>
                    navigate(`/dental/${id}/procedure/${cell.id}/print`)
                  }
                  icon={<PrinterOutlined />}
                  title="Imprimir / Assinar procedimento"
                />
              </Space>
            ),
          },
        ]}
        dataSource={filteredItems}
        rowKey="id"
        pagination={false}
        bordered
        locale={{
          emptyText: 'Nenhum procedimento encontrado para este tratamento.',
        }}
        title={() => (
          <Row style={{ display: 'flex', justifyContent: 'start' }}>
            <ATitle>Procedimentos inclusos</ATitle>
          </Row>
        )}
        footer={() => {
          return (
            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
              <ATitle>Total</ATitle>
              <ATitle>{currency(treatment?.total || 0)}</ATitle>
            </Row>
          );
        }}
      />
      {renderModalEditEvolution()}
      {renderModalHistoricTreatment()}
    </>
  );
};

export default DentalTreatmentEvolution;
